import { firstElement } from '@core/util/array';
import { getInitials } from '@core/util/business-user';
import { BusinessUser } from '@models/businesses';
import { Selector } from '@ngxs/store';
import { AuthState } from '@state/auth';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { SelectedMatter } from '@state/matter/matter.selectors';
import {
	UserSession,
	UserSessionsState,
} from '@state/user-sessions/user-sessions.state';

export interface MatterActivity {
	users: MatterActivityUser[];
	totalUsers: number;
	totalActiveUsers: number;
}

export interface MatterActivityUser {
	userId: string;
	userName: string;
	initials: string;
	firstInitial: string;
	currentUser: boolean;
	active: boolean;
	lastUpdated: number;
	url: string;
}

export class MatterActivitySelectors {
	@Selector([
		SelectedMatter.id,
		SelectedMatter.lastViewed,
		UserSessionsState.getMostRecentSessions,
		BusinessUsersState.getActiveUsers,
		AuthState.getUserId,
		AuthState.getPhotoUrl,
	])
	static get(
		matterId: string,
		matterLastViewed: { [userId: string]: number },
		sessions: UserSession[],
		businessUsers: BusinessUser[],
		currentUserId: string,
		photoURL: string,
	): MatterActivity {
		const result: MatterActivity = {
			users: [],
			totalUsers: 0,
			totalActiveUsers: 0,
		};

		if (
			!(businessUsers?.length > 0 && sessions?.length > 0 && matterLastViewed)
		) {
			return result;
		}

		const currentActiveUsersInMatter = sessions.filter(
			(session) => session.matterId === matterId && session.status === 'online',
		);

		result.users = Object.entries(matterLastViewed)
			.map(([key, value]) => {
				const session = firstElement(
					currentActiveUsersInMatter
						.filter((x) => x.userId === key)
						.sort((a, b) => a.timestamp - b.timestamp),
				);
				const user = businessUsers.find((x) => x.id === key);

				if (!user) {
					return null;
				}

				const intials = getInitials(user);

				return {
					userId: user.id,
					userName: `${user.firstName} ${user.lastName}`,
					initials: getInitials(user),
					firstInitial: intials[0],
					currentUser: user.id === currentUserId,
					active: !!session,
					lastUpdated: value,
					url: session?.url,
					avatar: user.id === currentUserId ? photoURL : null,
				};
			})
			.filter((x) => !!x)
			.sort((a, b) => a.lastUpdated - b.lastUpdated);

		result.totalUsers = result.users.length;
		result.totalActiveUsers = result.users.filter((x) => x.active).length;

		return result;
	}
}
